import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 300;

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    /* Hover desktop */
    this.addEventListener('pointerover', this.activate, {
      signal: this.#abortController.signal,
    });

    this.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    /* Click mobile */
    this.addEventListener('click', (e) => {
      const item = findMenuItem(e.target);
      if (!item) return;

      if (this.#state.activeItem === item) {
        this.#deactivate(item);
      } else {
        this.#activateHandler(e);
      }
    }, { signal: this.#abortController.signal });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #state = {
    activeItem: null,
  };

  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  get overflowMenu() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]') || null;
  }

  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();
    this.#debouncedActivateHandler(event);
  };

  #activateHandler = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item || item === this.#state.activeItem) return;

    this.dispatchEvent(new MegaMenuHoverEvent());

    if (this.#state.activeItem) {
      this.#state.activeItem.ariaExpanded = 'false';
      this.#state.activeItem.classList.remove('is-active');
    }

    this.#state.activeItem = item;

    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    item.classList.add('is-active');

    let submenu = findSubmenu(item);

    /* 🔥 fuerza submenu aunque esté en block */
    if (!submenu) {
      submenu = item.querySelector('.mega-menu, .submenu, [data-mega]');
    }

    if (!submenu) return;

    submenu.style.display = 'block';
    submenu.style.opacity = '1';
    submenu.style.pointerEvents = 'auto';
    submenu.style.visibility = 'visible';

    const h = submenu.offsetHeight;

    this.style.setProperty('--submenu-height', `${h}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item) return;

    let submenu = findSubmenu(item);
    if (!submenu) {
      submenu = item.querySelector('.mega-menu, .submenu, [data-mega]');
    }

    if (submenu) {
      submenu.style.opacity = '0';
      submenu.style.pointerEvents = 'none';
      submenu.style.visibility = 'hidden';
      submenu.style.display = 'none';
    }

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');

    item.ariaExpanded = 'false';
    item.classList.remove('is-active');

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
  };

  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/* ================= HELPERS ================= */

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  return element.closest('[ref="menuitem"], .menu-item, [data-menu-item]');
}

function findSubmenu(element) {
  if (!element) return null;

  return (
    element.querySelector('[ref="submenu[]"]') ||
    element.querySelector('.mega-menu') ||
    element.querySelector('.submenu') ||
    element.querySelector('[data-mega]')
  );
}